/**
 * Data layer for `advertiser-approval`. Thin typed fetch wrappers over
 * /api/v1/advertiser-approval. Raw `fetch()` is confined to this file per the
 * dash contract.
 *
 * Backend surface (apps/api/src/routes/advertiser-approval.ts):
 *   GET  /queue/:advertiser_id?status=&campaign_id=&from=&to=&limit=
 *   POST /approve/:id   {}                       -> 200 decision
 *   POST /reject/:id    { reason: string }       -> 200 decision
 *   GET  /audit/:advertiser_id?from=&to=&limit=
 */

export type ApprovalStatus = "pending" | "approved" | "rejected";

export interface ApprovalRow {
	id: string;
	tenant_id: string;
	advertiser_id: string;
	conversion_id: string;
	campaign_id: string | null;
	status: ApprovalStatus;
	reviewed_by: string | null;
	reviewed_at: number | null;
	reason: string | null;
	created_at: number;
}

export interface ApprovalAuditRow {
	id: string;
	tenant_id: string;
	approval_id: string;
	advertiser_id: string;
	conversion_id: string;
	action: string;
	actor_id: string | null;
	reason: string | null;
	created_at: number;
}

export interface QueueFilters {
	status: string;
	campaign_id: string | null;
	from: number | null;
	to: number | null;
	limit: number;
}

export interface QueueResponse {
	count: number;
	filters: QueueFilters;
	items: ApprovalRow[];
}

export interface AuditResponse {
	count: number;
	filters: { from: number | null; to: number | null; limit: number };
	items: ApprovalAuditRow[];
}

export interface DecisionResponse {
	id: string;
	advertiser_id: string;
	conversion_id: string;
	status: ApprovalStatus;
	reviewed_by: string | null;
	reviewed_at: number;
	reason?: string;
}

export interface QueueParams {
	advertiserId: string;
	status?: "pending" | "approved" | "rejected" | "all";
	campaignId?: string | null;
	from?: number | null;
	to?: number | null;
	limit?: number;
}

const BASE = "/api/v1/advertiser-approval";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function buildQueueUrl(p: QueueParams): string {
	const qs = new URLSearchParams();
	if (p.status) qs.set("status", p.status);
	if (p.campaignId) qs.set("campaign_id", p.campaignId);
	if (p.from != null) qs.set("from", String(p.from));
	if (p.to != null) qs.set("to", String(p.to));
	if (p.limit != null) qs.set("limit", String(p.limit));
	const tail = qs.toString();
	return `${BASE}/queue/${encodeURIComponent(p.advertiserId)}${tail ? `?${tail}` : ""}`;
}

export async function fetchQueue(params: QueueParams): Promise<QueueResponse> {
	const res = await fetch(buildQueueUrl(params), {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<QueueResponse>(res);
}

export async function approve(id: string): Promise<DecisionResponse> {
	const res = await fetch(`${BASE}/approve/${encodeURIComponent(id)}`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({}),
	});
	return parseOrThrow<DecisionResponse>(res);
}

export async function reject(id: string, reason: string): Promise<DecisionResponse> {
	const res = await fetch(`${BASE}/reject/${encodeURIComponent(id)}`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({ reason }),
	});
	return parseOrThrow<DecisionResponse>(res);
}

export interface AuditParams {
	advertiserId: string;
	from?: number | null;
	to?: number | null;
	limit?: number;
}

function buildAuditUrl(p: AuditParams): string {
	const qs = new URLSearchParams();
	if (p.from != null) qs.set("from", String(p.from));
	if (p.to != null) qs.set("to", String(p.to));
	if (p.limit != null) qs.set("limit", String(p.limit));
	const tail = qs.toString();
	return `${BASE}/audit/${encodeURIComponent(p.advertiserId)}${tail ? `?${tail}` : ""}`;
}

export async function fetchAudit(params: AuditParams): Promise<AuditResponse> {
	const res = await fetch(buildAuditUrl(params), {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<AuditResponse>(res);
}

export const advertiserApprovalQueryKeys = {
	all: ["advertiser-approval"] as const,
	queue: (advertiserId: string, status: string) =>
		[...advertiserApprovalQueryKeys.all, "queue", advertiserId, status] as const,
	audit: (advertiserId: string) =>
		[...advertiserApprovalQueryKeys.all, "audit", advertiserId] as const,
	detail: (id: string) => [...advertiserApprovalQueryKeys.all, id] as const,
};
